import React, { useState, useRef } from 'react';
import { CreditCard, Calendar, Clock, Printer, FileDown } from 'lucide-react';
import { useVendorPayments } from '../../hooks/useVendorPayments';
import { openPrintWindow, saveDomAsPdf, escapeHtml } from '../../utils/reportExport';
import { useToastContext } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import PrivateBookingBadge from '../../components/shared/PrivateBookingBadge';
import { isPrivateBooking, PRIVATE_PAYOUT_EXCLUDED_NOTE } from '../../utils/bookingSource';

const VendorPayments = () => {
  const reportRef = useRef(null);
  const { user } = useAuth();
  const { success, error: showError } = useToastContext();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  
  const {
    payments,
    isLoading,
    error,
    pagination,
    formatAmount,
    getStatusIcon,
    getStatusColor
  } = useVendorPayments(statusFilter, currentPage);

  // Format date to be more readable
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /** Payout shown to vendor: amount minus platform fee (see API `vendor_payout`). */
  const vendorPayoutAmount = (payment) => {
    if (isPrivateBooking(payment) || payment?.counts_toward_platform_payout === false) {
      return 0;
    }
    if (payment?.vendor_payout != null && payment.vendor_payout !== '') {
      const v = parseFloat(payment.vendor_payout);
      if (!Number.isNaN(v)) return payment.vendor_payout;
    }
    const gross = parseFloat(payment?.amount);
    const fee = parseFloat(payment?.platform_fee ?? 0);
    if (!Number.isNaN(gross)) {
      if (!Number.isNaN(fee) && fee > 0) {
        return Math.max(0, gross - fee);
      }
      return payment?.amount;
    }
    return payment?.amount ?? 0;
  };

  const displayPaymentAmount = (payment) => {
    if (isPrivateBooking(payment)) {
      return payment?.amount ?? 0;
    }
    return vendorPayoutAmount(payment);
  };

  const fmtAmount = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(v);
    if (Number.isNaN(n)) return '0.00';
    return n.toFixed(2);
  };

  const buildPrintBodyHtml = () => {
    const vendorLine = user?.name || user?.email ? escapeHtml(user.name || user.email) : '';
    const filterLabel =
      statusFilter === 'all'
        ? 'All payments'
        : `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} payments`;
    const pageLine =
      pagination && pagination.last_page > 1
        ? `List: page ${pagination.current_page} of ${pagination.last_page} (${pagination.total ?? ''} total).`
        : '';

    const rowsHtml = payments
      .map((payment) => {
        const payout = fmtAmount(displayPaymentAmount(payment));
        const privateLabel = isPrivateBooking(payment) ? 'Private' : '';
        const status = escapeHtml(payment.status || '');
        const when = escapeHtml(`${formatDate(payment.created_at)} ${formatTime(payment.created_at)}`);
        const booking = escapeHtml(payment.booking?.booking_reference || (payment.booking_id ? `#${payment.booking_id}` : '—'));
        const client = escapeHtml(payment.booking?.client?.name || '—');
        const ref = escapeHtml(payment.payment_reference || payment.transaction_id || '—');
        return `<tr>
          <td><strong>$${escapeHtml(String(payout))}</strong>${privateLabel ? ` <span style="font-size:10px;color:#4338ca">(${privateLabel})</span>` : ''}</td>
          <td>${status}</td>
          <td>${when}</td>
          <td>${booking}</td>
          <td>${client}</td>
          <td style="font-size:10px">${ref}</td>
        </tr>`;
      })
      .join('');

    return `
      <h1>Payments</h1>
      <p class="muted">${vendorLine ? `Vendor: ${vendorLine}<br/>` : ''}${escapeHtml(filterLabel)} · Generated ${escapeHtml(
        new Date().toLocaleString()
      )}</p>
      ${pageLine ? `<p class="muted">${escapeHtml(pageLine)}</p>` : ''}
      <table>
        <thead>
          <tr><th>Your payout</th><th>Status</th><th>Date & time</th><th>Booking</th><th>Client</th><th>Reference</th></tr>
        </thead>
        <tbody>
          ${rowsHtml || '<tr><td colspan="6">No payments on this page.</td></tr>'}
        </tbody>
      </table>
    `;
  };

  const handlePrint = () => {
    const r = openPrintWindow('Payments', buildPrintBodyHtml());
    if (!r.ok) showError('Allow pop-ups to print this report.');
  };

  const handlePdf = async () => {
    if (!reportRef.current) return;
    setPdfBusy(true);
    try {
      await saveDomAsPdf(reportRef.current, `vendor-payments-${new Date().toISOString().slice(0, 10)}`);
      success('PDF downloaded.');
    } catch (e) {
      showError(e?.message || 'Could not generate PDF.');
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <>
         <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
         <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Payments</h1>
            <p className="text-sm text-gray-600">View and manage your payment history. Platform payouts exclude private bookings.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4 sm:mt-0">
            <button
              type="button"
              onClick={handlePrint}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 bg-white text-gray-800 rounded-lg hover:bg-gray-50 transition-colors text-sm w-full sm:w-auto justify-center"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
            <button
              type="button"
              onClick={handlePdf}
              disabled={pdfBusy || isLoading}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm w-full sm:w-auto justify-center disabled:opacity-50"
            >
              <FileDown className="w-4 h-4" />
              <span>{pdfBusy ? 'PDF…' : 'Download PDF'}</span>
            </button>
          </div>
        </div>

        <div ref={reportRef} className="space-y-6">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xl font-bold text-gray-900">Payments</p>
          {(user?.name || user?.email) && (
            <p className="text-sm text-gray-600 mt-1">{user.name || user.email}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Filter:{' '}
            {statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} ·{' '}
            {new Date().toLocaleString()}
          </p>
        </div>
        {/* Tab Filters */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8" aria-label="Payment status tabs">
            <button
              onClick={() => {
                setStatusFilter('all');
                setCurrentPage(1);
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                statusFilter === 'all'
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              All Payments
            </button>
            <button
              onClick={() => {
                setStatusFilter('completed');
                setCurrentPage(1);
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                statusFilter === 'completed'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Completed
            </button>
            <button
              onClick={() => {
                setStatusFilter('pending');
                setCurrentPage(1);
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                statusFilter === 'pending'
                  ? 'border-yellow-600 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => {
                setStatusFilter('failed');
                setCurrentPage(1);
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                statusFilter === 'failed'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Failed
            </button>
          </nav>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            Failed to load payments. Please try again.
          </div>
        ) : payments.length > 0 ? (
          <div className="space-y-4">
            {payments.map((payment) => {
              const isPrivate = isPrivateBooking(payment);
              return (
              <div key={payment.id} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <CreditCard className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-gray-900">
                            ${formatAmount(displayPaymentAmount(payment))}
                          </p>
                          {isPrivate && <PrivateBookingBadge />}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {isPrivate ? 'Collected offline' : 'Your payout'}
                        </p>
                        {isPrivate && (
                          <p className="text-xs text-indigo-600 mt-0.5">{PRIVATE_PAYOUT_EXCLUDED_NOTE}</p>
                        )}
                        <p className="text-sm text-gray-500">
                          {payment.booking_id ? `Booking #${payment.booking_id}` : 'Payment'}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          {formatDate(payment.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          {formatTime(payment.created_at)}
                        </span>
                      </div>
                      {payment.payment_method && (
                        <span className="text-gray-600 capitalize">
                          {payment.payment_method}
                        </span>
                      )}
                      {payment.payment_reference && (
                        <span className="text-gray-500 text-xs font-mono">
                          Ref: {payment.payment_reference.substring(0, 8)}
                        </span>
                      )}
                    </div>
                    {/* Additional Payment Details */}
                    <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600">
                      {payment.booking?.booking_reference && (
                        <div>
                          <span className="font-medium">Booking:</span> {payment.booking.booking_reference}
                        </div>
                      )}
                      {payment.booking?.client && (
                        <div>
                          <span className="font-medium">Client:</span> {payment.booking.client.name}
                        </div>
                      )}
                      {payment.payment_type && (
                        <div>
                          <span className="font-medium">Type:</span> <span className="capitalize">{payment.payment_type.replace('_', ' ')}</span>
                        </div>
                      )}
                      {payment.currency && (
                        <div>
                          <span className="font-medium">Currency:</span> {payment.currency.toUpperCase()}
                        </div>
                      )}
                      {(parseFloat(payment.booking?.discount_amount) || 0) > 0 && (
                        <div>
                          <span className="font-medium">Discount{payment.booking?.discount_code?.code ? ` (${payment.booking.discount_code.code})` : ''}:</span>{' '}
                          <span className="text-green-600">-${formatAmount(payment.booking.discount_amount)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2 ${getStatusColor(payment.status)}`}>
                      {getStatusIcon(payment.status)}
                      {payment.status || 'unknown'}
                    </span>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-8 sm:p-12 text-center">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No payments found</p>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-700">
              Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} results
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={pagination.current_page === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Previous
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                  let page;
                  if (pagination.last_page <= 5) {
                    page = i + 1;
                  } else if (pagination.current_page <= 3) {
                    page = i + 1;
                  } else if (pagination.current_page >= pagination.last_page - 2) {
                    page = pagination.last_page - 4 + i;
                  } else {
                    page = pagination.current_page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm border rounded-lg transition-colors ${
                        pagination.current_page === page
                          ? 'bg-black text-white border-black'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(pagination.last_page, prev + 1))}
                disabled={pagination.current_page === pagination.last_page}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
        </div>
        </div>
        </div>
    </>
  );
};

export default VendorPayments;

